import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Loading from 'components/pure/utils/Loading';
import noop from 'lodash/noop';
import ToastActions from '@store/toast/ToastActions';
import AuthStore from 'stores/AuthStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import confirm from 'utils/confirm';
import TriggerRuleActions from 'actions/triggers/TriggerRuleActions';
import UserActions from 'actions/UserActions';
import TriggerRuleStore from 'stores/triggers/TriggerRuleStore';
import TeamActions from 'actions/TeamActions';
import Button from '@material-ui/core/Button';
import pick from 'lodash/pick';
import UserStore from 'stores/UserStore';
import RulePermissionsTable from './RulePermissionsTable';
import RulePermissionCreator from './RulePermissionCreator';
import DepartmentStore from 'stores/department/DepartmentStore';
import TeamListStore from 'stores/TeamListStore';
import {unsavedMessage} from 'utils/const';

@connectToStores
export default class RulePermissionsDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.onRequestClose = this.onRequestClose.bind(this);
    this.onSave = this.onSave.bind(this);
  }

  static getStores() {
    return [AuthStore, TriggerRuleStore, UserStore, DepartmentStore, TeamListStore];
  }

  static getPropsFromStores() {
    return {
      ...pick(TriggerRuleStore.getState(), [
        'rulePermissions',
        'rulePermissionsLoading',
        'rulePermissionsErrored',
      ]),
      ...pick(UserStore.getState(), ['users', 'usersLoading', 'usersErrored']),
      ...pick(TeamListStore.getState(), ['teams']),
      ...pick(DepartmentStore.getState(), [
        'departmentProfiles',
        'areDepartmentProfilesLoading',
        'wasDepartmentProfilesFetchError',
      ]),
      sessionUser: AuthStore.getState().user,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  async loadData() {
    const sessionUser = AuthStore.getState().user;
    const isAdmin = sessionUser.isAdmin();
    TeamActions.fetchAllTeams(isAdmin);
    const [, rulePermissions] = await TriggerRuleActions.fetchTriggerRulePermissions(
      this.props.rule.id
    );
    const tempPermissions = TriggerRuleStore.getState().stagedPermissions;
    const stagedPermissions = tempPermissions
      ? rulePermissions.push(tempPermissions)
      : rulePermissions;
    const activePermissionIDs = stagedPermissions
      .filter(p => p.active)
      .map(p => p.id)
      .toSet();
    const inactivePermissionIDs = stagedPermissions
      .filter(p => !p.active)
      .map(p => p.id)
      .toSet();
    this.setState({
      rulePermissions: stagedPermissions,
      activePermissionIDs: activePermissionIDs,
      inactivePermissionIDs: inactivePermissionIDs,
    });
    UserActions.fetchAll({
      ids: rulePermissions.map(perm => perm.userId).filter(id => !!id),
    });
    TriggerRuleActions.clearStagedPermissions();
  }

  onRequestClose() {
    const {onRequestClose, rulePermissions, rule} = this.props;
    if (this.state.rulePermissions !== rulePermissions.get(rule.id)) {
      confirm(unsavedMessage).then(onRequestClose, noop);
    } else {
      onRequestClose();
    }
  }

  /**
   * Filter out inactive permissions that were not deleted by the user. A user clicking the Trash
   * button is an explicit action that means "Remove this share, even if the user has an inactive Notfiy"
   * @param permissions the permissions after the user is done but before we send to the BE
   * @returns the filtered permissions
   */
  filterForSave(permissions) {
    return permissions.filter(
      p =>
        p.active || // active ones are always sent
        // past this point the permission is inactive
        (!p.active &&
          // if deleted and the permission was there initially, we send to the BE, because we are explicitly "deleting"
          // it, which translate to whether we will deactivate a share
          ((p.deleted && this.state.inactivePermissionIDs.contains(p.id)) ||
            // OR if the permission was originally active, then of course we will let the BE know
            this.state.activePermissionIDs.contains(p.id)))
    );
  }

  async onSave() {
    try {
      await TriggerRuleActions.updateTriggerRulePermissions(
        this.props.rule.id,
        this.filterForSave(this.state.rulePermissions)
      );
      this.props.onSaveSuccess();
      ToastActions.addToast({
        message: 'Trigger permissions saved successfully!',
      });
      this.props.onRequestClose();
    } catch (e) {
      ToastActions.addToast({
        type: 'error',
        message: 'There was an error saving the trigger permissions.',
      });
    }
  }

  render() {
    const {rule} = this.props;
    const {rulePermissions} = this.state;
    return (
      <Dialog className="rule-permissions-dialog" open maxWidth="md">
        <ClosableDialogTitle onClose={this.onRequestClose}>Sharing</ClosableDialogTitle>
        <DialogContent>
          {this.props.rulePermissionsLoading.get(rule.id) ? (
            <Loading />
          ) : this.props.rulePermissionsErrored.get(rule.id) ? (
            <div className="ev-error-message">There was an error loading rule shares.</div>
          ) : rulePermissions ? (
            <div>
              <RulePermissionCreator
                {...this.props}
                rulePermissions={rulePermissions}
                onUpdate={rulePermissions => this.setState({rulePermissions})}
                sessionUser={this.props.sessionUser}
              />
              <RulePermissionsTable
                {...this.props}
                rulePermissions={rulePermissions}
                onUpdate={rulePermissions => this.setState({rulePermissions})}
              />
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={this.onRequestClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={this.onSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
